import { avatarHelper } from "../../helpers/avatarHelper.js";

/**
 * ContactPicker — renders inline into the chat area's message container.
 * Shows AI agents + users (when enableUserParticipants is true) for new-chat or add-member flows.
 * Not a ComponentBase — pure DOM, controlled by WorkspaceChatArea.
 */

const STYLES = `
    .ws-cp-wrap {
        display: flex; flex-direction: column; height: 100%;
        padding: 1.5rem; animation: cpFadeIn var(--ab-t-slow);
        background: var(--bs-body-bg);
        border-radius: var(--ab-radius-lg);
        box-shadow: var(--ab-shadow-lg);
    }
    @keyframes cpFadeIn { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }
    .ws-cp-top {
        display: flex; align-items: center; gap: 0.75rem; margin-bottom: 1rem; flex-shrink: 0;
    }
    .ws-cp-back {
        flex-shrink: 0;
    }
    .ws-cp-heading {
        font-size: var(--ab-text-md);
        font-weight: var(--ab-weight-bold);
        color: var(--bs-emphasis-color);
    }
    .ws-cp-search {
        width: 100%; padding: 0.55rem 0.75rem 0.55rem 2.2rem;
        border: 1px solid var(--bs-border-color-translucent);
        border-radius: var(--ab-radius-pill);
        background: var(--bs-tertiary-bg); color: var(--bs-body-color);
        font-size: var(--ab-text-base); outline: none;
        transition: border-color var(--ab-t-base), box-shadow var(--ab-t-base);
        flex-shrink: 0;
    }
    .ws-cp-search:focus-visible {
        border-color: var(--ab-focus-border);
        box-shadow: var(--ab-focus-ring);
    }
    .ws-cp-search-wrap { position: relative; margin-bottom: 1rem; flex-shrink: 0; }
    .ws-cp-search-wrap i { position: absolute; left: 0.75rem; top: 50%; transform: translateY(-50%); color: var(--bs-secondary-color); font-size: var(--ab-text-xs); }
    .ws-cp-list { flex-grow: 1; overflow-y: auto; }
    .ws-cp-section-title {
        font-size: var(--ab-label-size);
        font-weight: var(--ab-label-weight);
        letter-spacing: var(--ab-label-spacing);
        text-transform: uppercase;
        color: var(--ab-label-color);
        padding: 0.5rem 0 0.35rem;
        margin-bottom: 0.1rem;
    }
    .ws-cp-item {
        display: flex; align-items: center; gap: 0.7rem;
        padding: 0.6rem 0.5rem;
        border-radius: var(--ab-radius-sm);
        cursor: pointer;
        transition: background-color var(--ab-t-fast), color var(--ab-t-fast);
        outline: none;
    }
    @media (hover: hover) {
        .ws-cp-item:hover { background: var(--ab-hover-bg); }
    }
    .ws-cp-item:focus-visible {
        box-shadow: var(--ab-focus-ring);
    }
    .ws-cp-item:active {
        background: var(--ab-hover-bg);
        opacity: 0.8;
    }
    .ws-cp-av {
        width: 40px; height: 40px; border-radius: var(--ab-radius-full); flex-shrink: 0;
        display: flex; align-items: center; justify-content: center;
        font-size: var(--ab-text-sm); font-weight: var(--ab-weight-bold); color: #fff;
    }
    .ws-cp-item-info { flex-grow: 1; min-width: 0; }
    .ws-cp-item-name {
        font-size: var(--ab-text-base);
        font-weight: var(--ab-weight-medium);
        color: var(--bs-body-color);
    }
    .ws-cp-item-sub { font-size: var(--ab-text-xs); color: var(--bs-secondary-color); }
    .ws-cp-empty { text-align: center; padding: 2rem 1rem; color: var(--bs-secondary-color); font-size: var(--ab-text-sm); }
`;

let _stylesInjected = false;
function injectStyles() {
    if (_stylesInjected) return;
    const el = document.createElement('style');
    el.id = 'ws-contact-picker-styles';
    el.textContent = STYLES;
    document.head.appendChild(el);
    _stylesInjected = true;
}

export class ContactPicker {
    /**
     * @param {Object} opts
     * @param {Object} opts.workspace
     * @param {Object} opts.appClient
     * @param {Function} opts.onSelect  - called with { type: 'agent'|'user', id, displayName, avatarUrl }
     * @param {Function} opts.onCancel
     * @param {string}   [opts.mode]    - 'new_chat' (default) | 'add_member'
     */
    constructor(opts) {
        this.workspace = opts.workspace;
        this.appClient = opts.appClient;
        this.onSelect = opts.onSelect;
        this.onCancel = opts.onCancel;
        this.mode = opts.mode || 'new_chat';
        this._agents = [];
        this._users = [];
        this._query = '';
        this.el = null;
        injectStyles();
    }

    async mount(container) {
        this.container = container;
        this._agents = (this.workspace.agents || []).map(a => ({
            type: 'agent', id: a.name, displayName: a.name, avatarUrl: a.avatarUrl || null,
            sub: a.description || 'AI Agent',
        }));

        const enableParticipants = this.workspace.config?.options?.enableUserParticipants !== false;
        if (enableParticipants) {
            try {
                const currentUserId = this.appClient.getSharedData('user')?._id;
                // Use the live RDS 'users' dataset (no permission barrier, available to all)
                const allUsers = await this.appClient.rds.getAll('users');
                this._users = (allUsers || [])
                    .filter(u => u._id !== currentUserId)
                    .map(u => ({
                        type: 'user',
                        id: u._id,
                        displayName: [u.firstName, u.lastName].filter(Boolean).join(' ') || u.username || u.email || u._id,
                        avatarUrl: u.profilePicture || u.avatarUrl || null,
                        sub: u.username || u.email || '',
                    }));
            } catch (e) {
                console.warn('[ContactPicker] Could not load users from RDS:', e.message);
            }
        }

        this._render();
    }

    t(text, replacements) {
        return this.appClient.tx.t(text, replacements);
    }

    _render() {
        if (!this.container) return;
        const heading = this.mode === 'add_member' ? this.t('Add Participant') : this.t('New Conversation');
        const showBack = this.mode === 'add_member' || !!this.workspace.currentThreadId;

        if (!this.el) {
            this.el = document.createElement('div');
            this.el.className = 'ws-cp-wrap';
            this.el.innerHTML = `
                <div class="ws-cp-top">
                    ${showBack ? `<button class="ab-icon-btn ab-icon-btn-sm ws-cp-back" id="cp-back" title="${this.escapeHtml(this.t('Back'))}" aria-label="${this.escapeHtml(this.t('Back'))}"><i class="bi bi-arrow-left"></i></button>` : ''}
                    <span class="ws-cp-heading">${heading}</span>
                </div>
                <div class="ws-cp-search-wrap">
                    <i class="bi bi-search"></i>
                    <input class="ws-cp-search" id="cp-search" placeholder="${this.escapeHtml(this.t('Search people or agents…'))}" value="${this.escapeHtml(this._query)}" autocomplete="off">
                </div>
                <div class="ws-cp-list" id="cp-list" role="listbox" aria-label="${this.escapeHtml(heading)}"></div>
            `;

            const backBtn = this.el.querySelector('#cp-back');
            if (backBtn) {
                backBtn.addEventListener('click', () => this.onCancel?.());
            }

            const searchEl = this.el.querySelector('#cp-search');
            searchEl.addEventListener('input', e => {
                this._query = e.target.value;
                this._render();
            });

            this.container.appendChild(this.el);
            requestAnimationFrame(() => searchEl.focus());
        }

        const q = this._query.toLowerCase();
        const agents = this._agents.filter(a => !q || a.displayName.toLowerCase().includes(q));
        const users  = this._users.filter(u => !q || u.displayName.toLowerCase().includes(q) || (u.sub||'').toLowerCase().includes(q));

        const list = this.el.querySelector('#cp-list');
        list.innerHTML = '';

        if (agents.length > 0) {
            const title = document.createElement('div');
            title.className = 'ws-cp-section-title';
            title.textContent = this.t('AI Agents');
            list.appendChild(title);
            agents.forEach(a => list.appendChild(this._buildItem(a)));
        }

        if (users.length > 0) {
            const title = document.createElement('div');
            title.className = 'ws-cp-section-title';
            title.textContent = this.t('Users');
            list.appendChild(title);
            users.forEach(u => list.appendChild(this._buildItem(u)));
        }

        if (agents.length === 0 && users.length === 0) {
            list.innerHTML = `<div class="ws-cp-empty">${this.t('No results for "{{query}}"', { query: this._query })}</div>`;
        }
    }

    _buildItem(contact) {
        const row = document.createElement('div');
        row.className = 'ws-cp-item';
        row.setAttribute('tabindex', '0');
        row.setAttribute('role', 'option');
        row.setAttribute('aria-label', contact.displayName);

        const avEl = avatarHelper.createAvatarElement({
            displayName: contact.displayName,
            avatarUrl: contact.avatarUrl || null,
            sizeClass: 'medium',
            type: contact.type === 'agent' ? 'agent' : 'user',
        });

        row.innerHTML = `
            ${avEl.outerHTML}
            <div class="ws-cp-item-info">
                <div class="ws-cp-item-name">${this.escapeHtml(contact.displayName)}</div>
                <div class="ws-cp-item-sub">${this.escapeHtml(contact.sub || '')}</div>
            </div>
            <i class="bi bi-chevron-right text-secondary" style="font-size:var(--ab-text-2xs);" aria-hidden="true"></i>
        `;
        row.addEventListener('click', () => this.onSelect?.(contact));
        row.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                this.onSelect?.(contact);
            }
        });
        return row;
    }


    unmount() {
        this.el?.remove();
        this.el = null;
    }

    escapeHtml(s) {
        return String(s||'').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
    }
}
